import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Clock, Star, Receipt, Monitor, ChefHat, Download, LayoutGrid, PhoneCall, TrafficCone, Timer } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import PosTerminalySprava from '../PosTerminalySprava';
import KdsStaniceSprava from '../KdsStaniceSprava';
import MinutkySprava from '../MinutkySprava';
import KeStazeni from '../KeStazeni';
import DopravniInfoSprava from './DopravniInfoSprava';
import UctenkyNastaveni from '../UctenkyNastaveni';
import VetyHovoru from '../VetyHovoru';
import ProvozovnaAdresa from './ProvozovnaAdresa';
import EditorStoluSprava from '../stoly/EditorStoluSprava';
import { PracovniDobaSprava, SvatkySprava } from './ProvozovnaKalendar';
import { muze } from '../../lib/moduly';
import { SECTION_COLOR, CERVENA, ZELENA, nacti, tlumene, cara, Nadpis, RadekPanelu } from './ui';

/**
 * Správa → Provozovna. Norma §1: seznam sekcí provozovny a jejích zařízení
 * · detail sekce ve sloupci · třetí sloupec `panelVzdy` se stavem zařízení
 * a „K dořešení" (pokladna offline, zařízení čeká na registraci, chybí GPS).
 *
 * Provozovna je samostatná stránka i kvůli Uzlu pobočky: až přibude druhá
 * pobočka, nad sekce přibude výběr provozovny a nic se nestěhuje podruhé.
 *
 * Dřív: Nastavení → Směny (pracovní doba, svátky), Provozní náklady (lokace),
 * Kuchyně a bar, Pokladny, Ke stažení a Účtenky — šest položek ve dvou
 * skupinách. Stoly a půdorys (2026-10-20) — editor z pokladny, online. Pokladny, KDS a účtenka zůstávají ve svých komponentách,
 * dorovnané na normu: ModalShell, usePotvrzeni, přepínače, barva sekce z `barva`.
 */
const SEKCE = [
    { id: 'adresa', skupina: 'Provozovna', nazev: 'Adresa a poloha', Icon: MapPin },
    { id: 'doba', skupina: 'Provozovna', nazev: 'Otevírací doba', Icon: Clock },
    { id: 'svatky', skupina: 'Provozovna', nazev: 'Svátky a zavřeno', Icon: Star },
    { id: 'uctenka', skupina: 'Provozovna', nazev: 'Účtenka', Icon: Receipt },
    { id: 'stoly', skupina: 'Provozovna', nazev: 'Stoly a půdorys', Icon: LayoutGrid },
    { id: 'hovor', skupina: 'Provozovna', nazev: 'Průvodce hovorem', Icon: PhoneCall },
    { id: 'pokladny', skupina: 'Zařízení', nazev: 'Pokladny a telefon', Icon: Monitor },
    { id: 'kds', skupina: 'Zařízení', nazev: 'Kuchyně a bar', Icon: ChefHat },
    { id: 'minutky', skupina: 'Zařízení', nazev: 'Minutky', Icon: Timer },
    { id: 'stazeni', skupina: 'Zařízení', nazev: 'Ke stažení', Icon: Download },
    { id: 'doprava', skupina: 'Rozvoz', nazev: 'Dopravní info', Icon: TrafficCone },
];

export function kDoreseniProvozovny({ terminaly, lokace }) {
    const out = [];
    if (lokace && lokace.lat == null) out.push({ id: 'gps', sekce: 'adresa', problem: true, titulek: 'Chybí GPS bistra', popis: 'Rozvoz nespočítá dojezdy.' });
    for (const t of terminaly?.terminaly || []) {
        if (!t.aktivni) continue;
        if (!t.sparovany) out.push({ id: `nesp-${t.id}`, sekce: 'pokladny', problem: false, titulek: `${t.nazev} · nespárováno`, popis: `Kód pro párování: ${t.kod || '—'}` });
        else if (!t.online) out.push({ id: `off-${t.id}`, sekce: 'pokladny', problem: true, titulek: `${t.nazev} je offline`, popis: t.posledni_signal_v ? `poslední signál ${new Date(t.posledni_signal_v).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'zatím se neozval' });
    }
    // Telefon pokladny (2026-10-13): když telefony jsou, pokladna bez telefonu nevolá a hovory nevidí
    const vsechny = terminaly?.terminaly || [];
    const telefony = vsechny.filter((t) => t.typ === 'telefon' && t.aktivni);
    if (telefony.length) {
        const bez = vsechny.filter((t) => t.typ !== 'telefon' && t.aktivni && !t.kds_stanice_id && !t.telefon_id);
        if (bez.length) out.push({ id: 'bez-telefonu', sekce: 'pokladny', problem: false, titulek: `${bez.length === 1 ? 'Pokladna' : 'Pokladny'} bez telefonu`, popis: `${bez.map((t) => t.nazev).join(', ')} — nevolá a hovory nevidí.` });
        if (!telefony.some((t) => t.vychozi_sms)) out.push({ id: 'bez-vychoziho', sekce: 'pokladny', problem: false, titulek: 'Žádný výchozí telefon pro SMS', popis: 'Automatické SMS půjdou přes telefon, který se ozval naposledy.' });
    }
    const nezname = terminaly?.nezname?.length || 0;
    if (nezname) out.push({ id: 'nezname', sekce: 'pokladny', problem: false, titulek: `${nezname} ${nezname === 1 ? 'zařízení čeká' : 'zařízení čekají'} na registraci`, popis: 'Ozvala se pokladna, kterou OS nezná.' });
    return out.sort((a, b) => Number(b.problem) - Number(a.problem));
}

export default function SpravaProvozovna({ user, isDark, isMobile, cardStyle, inputStyle, rezervaDole, onPrepni }) {
    const upravuje = muze(user, 'sprava.provozovna', 'upravuje');
    const [vybranaId, setVybranaId] = useState(null);
    const [hledani, setHledani] = useState('');
    const [terminaly, setTerminaly] = useState(null);
    const [lokace, setLokace] = useState(null);
    const [stanice, setStanice] = useState([]);

    const obnov = useCallback(() => {
        nacti('/api/pos-terminaly').then(setTerminaly).catch(() => setTerminaly(null));
        nacti('/api/konfigurace/bistro-lokace').then(setLokace).catch(() => {});
        nacti('/api/kds-stanice').then((d) => setStanice(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);
    useEffect(() => { obnov(); const t = setInterval(obnov, 30000); return () => clearInterval(t); }, [obnov]);
    // Po návratu z detailu (např. přidaná pokladna) obnov souhrn
    useEffect(() => { if (!vybranaId) obnov(); }, [vybranaId, obnov]);

    const kDoreseni = useMemo(() => kDoreseniProvozovny({ terminaly, lokace }), [terminaly, lokace]);
    const aktivniTerm = (terminaly?.terminaly || []).filter((t) => t.aktivni);
    const online = aktivniTerm.filter((t) => t.sparovany && t.online).length;
    const ml = tlumene(isDark);

    const meta = (id) => {
        if (id === 'adresa') return lokace ? (lokace.adresa || 'nevyplněno') : '';
        if (id === 'pokladny') return terminaly ? `${online} z ${aktivniTerm.length} online` : '';
        if (id === 'kds') return stanice.length ? `${stanice.filter((s) => s.aktivni).length} stanic` : '';
        if (id === 'minutky') {
            const zap = aktivniTerm.filter((t) => t.typ === 'pokladna' && t.minutky).length;
            return terminaly ? (zap ? `zapnuto na ${zap} ${zap === 1 ? 'pokladně' : 'pokladnách'}` : 'nikde nezapnuto') : '';
        }
        return '';
    };
    const problemSekce = new Set(kDoreseni.filter((k) => k.problem).map((k) => k.sekce));
    const q = hledani.trim().toLowerCase();
    const viditelne = SEKCE.filter((s) => !q || s.nazev.toLowerCase().includes(q));
    const vybrana = SEKCE.find((s) => s.id === vybranaId) || null;

    const seznam = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={ml} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat v provozovně…', placeholderKratky: 'Hledat' }}
                pod={isMobile && kDoreseni.length > 0 ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} styl={{ marginTop: 8 }}
                    renderAkce={(k) => <button type="button" onClick={() => setVybranaId(k.sekce)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} /> : null} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {['Provozovna', 'Zařízení'].map((sk) => {
                    const polozky = viditelne.filter((s) => s.skupina === sk);
                    if (!polozky.length) return null;
                    return (
                        <div key={sk}>
                            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: ml, margin: 0, padding: '12px 14px 6px' }}>{sk}</p>
                            {polozky.map((s) => {
                                const sel = s.id === vybranaId;
                                const m = meta(s.id);
                                return (
                                    <button key={s.id} type="button" onClick={() => setVybranaId(s.id)} className="w-full text-left flex items-center"
                                        style={{ gap: 12, padding: isMobile ? '12px 16px' : '10px 14px', minHeight: 56, cursor: 'pointer', border: 'none',
                                            borderBottom: `1px solid ${cara(isDark)}`, background: sel ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent',
                                            boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none' }}>
                                        <s.Icon size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                        <div className="flex-1 min-w-0">
                                            <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{s.nazev}</p>
                                            {m && <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{m}</p>}
                                        </div>
                                        {problemSekce.has(s.id) && <span aria-label="K dořešení" style={{ width: 8, height: 8, borderRadius: 4, background: CERVENA, flexShrink: 0 }} />}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => {
        if (!vybrana) return null;
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '6px 14px 10px' : '16px 18px 12px', borderBottom: `1px solid ${cara(isDark)}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranaId(null)} popis="Zpět na provozovnu" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                    <h2 className="font-bold flex items-center gap-2" style={{ fontSize: 18, margin: 0, color: 'var(--text-primary)' }}>
                        <vybrana.Icon size={17} style={{ color: SECTION_COLOR }} /> {vybrana.nazev}
                    </h2>
                    <p className="text-xs" style={{ color: ml, margin: '4px 0 0' }}>{vybrana.skupina}{meta(vybrana.id) ? ` · ${meta(vybrana.id)}` : ''}</p>
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '16px 18px' }}>
                    {vybrana.id === 'adresa' && <ProvozovnaAdresa lokace={lokace} onZmena={setLokace} upravuje={upravuje} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle} />}
                    {vybrana.id === 'doba' && <PracovniDobaSprava isDark={isDark} />}
                    {vybrana.id === 'svatky' && <SvatkySprava isDark={isDark} />}
                    {vybrana.id === 'stoly' && <EditorStoluSprava upravuje={upravuje} isDark={isDark} isMobile={isMobile} />}
                    {vybrana.id === 'uctenka' && <UctenkyNastaveni isDark={isDark} barva={SECTION_COLOR} onOtevritFirmu={muze(user, 'sprava.firma') ? () => onPrepni?.('firma') : null} />}
                    {vybrana.id === 'pokladny' && <PosTerminalySprava isDark={isDark} barva={SECTION_COLOR} />}
                    {vybrana.id === 'hovor' && <VetyHovoru isDark={isDark} />}
                    {vybrana.id === 'kds' && <KdsStaniceSprava isDark={isDark} barva={SECTION_COLOR} />}
                    {vybrana.id === 'minutky' && <MinutkySprava isDark={isDark} barva={SECTION_COLOR} />}
                    {vybrana.id === 'stazeni' && <KeStazeni isDark={isDark} />}
                    {vybrana.id === 'doprava' && <DopravniInfoSprava isDark={isDark} muzeUpravovat={upravuje} />}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
            <Nadpis isDark={isDark}>Zařízení</Nadpis>
            <RadekPanelu isDark={isDark} popis="Pokladny online" hodnota={terminaly ? `${online} / ${aktivniTerm.length}` : '—'} barva={terminaly && online < aktivniTerm.filter((t) => t.sparovany).length ? CERVENA : null} />
            <RadekPanelu isDark={isDark} popis="Čeká na registraci" hodnota={terminaly?.nezname?.length ?? '—'} />
            <RadekPanelu isDark={isDark} popis="Stanice KDS" hodnota={stanice.filter((s) => s.aktivni).length || '—'} />
            <RadekPanelu isDark={isDark} popis="Poloha bistra" hodnota={lokace ? (lokace.lat != null ? 'nastavena' : 'chybí') : '—'} barva={lokace && lokace.lat == null ? CERVENA : (lokace ? ZELENA : null)} />
            <Nadpis isDark={isDark} mt={16}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne="V provozovně je všechno v pořádku."
                renderAkce={(k) => <button type="button" onClick={() => setVybranaId(k.sekce)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranaId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam} detail={detail} panel={panel} />
        </div>
    );
}

